import { ParseResult } from '../types';

/**
 * Detects platform from URL and extracts playlist ID.
 * Supports:
 *   - https://open.spotify.com/playlist/{id}
 *   - https://open.spotify.com/playlist/{id}?si=...
 *   - spotify:playlist:{id}
 *   - https://music.youtube.com/playlist?list={id}
 *   - https://music.apple.com/{storefront}/playlist/{name}/pl.{id}
 *   - bare alphanumeric string (15+ chars) → defaults to spotify
 * Returns null if no pattern matches.
 */
export const parsePlaylistUrl = (input: string): ParseResult | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Spotify web URL: open.spotify.com/playlist/{id} with optional query params
  const spotifyWebPattern = /open\.spotify\.com\/playlist\/([a-zA-Z0-9]+)/;
  const spotifyWebMatch = trimmed.match(spotifyWebPattern);
  if (spotifyWebMatch) {
    return {
      platform: 'spotify',
      playlistId: spotifyWebMatch[1],
      originalUrl: trimmed,
    };
  }

  // Spotify URI: spotify:playlist:{id}
  const spotifyUriPattern = /spotify:playlist:([a-zA-Z0-9]+)/;
  const spotifyUriMatch = trimmed.match(spotifyUriPattern);
  if (spotifyUriMatch) {
    return {
      platform: 'spotify',
      playlistId: spotifyUriMatch[1],
      originalUrl: trimmed,
    };
  }

  // YouTube Music URL: music.youtube.com/playlist?list={id}
  // The list param can appear anywhere in the query string
  const ytMusicPattern = /music\.youtube\.com\/playlist\?(?:.*&)?list=([a-zA-Z0-9_-]+)/;
  const ytMusicMatch = trimmed.match(ytMusicPattern);
  if (ytMusicMatch) {
    return {
      platform: 'ytmusic',
      playlistId: ytMusicMatch[1],
      originalUrl: trimmed,
    };
  }

  // Apple Music URL: music.apple.com/{storefront}/playlist/{optional-name}/pl.{id}
  const appleMatch = trimmed.match(/music\.apple\.com\/([a-z]{2})\/playlist\/(?:[^/?#]+\/)?(pl\.[a-zA-Z0-9-]+)/i);
  if (appleMatch) {
    return {
      platform: 'apple',
      playlistId: appleMatch[2],
      originalUrl: trimmed,
      storefront: appleMatch[1].toLowerCase(),
    };
  }

  // Bare alphanumeric string (15+ chars) → default to spotify
  if (/^[a-zA-Z0-9]{15,}$/.test(trimmed)) {
    return {
      platform: 'spotify',
      playlistId: trimmed,
      originalUrl: trimmed,
    };
  }

  return null;
};
